import React, { useEffect, useState } from "react";
import { Button, Card, Icon } from "semantic-ui-react";
import { MenuIcon } from "lucide-react";
import styled from "styled-components";
import { FluidFormFields } from "../FormFields.component";
import { useFluidForm } from "../FormContext";
import { useRenderViewChange } from "../common";
import { SimpleFormClearButton } from "../SimpleFormClearButton";
// Most styling is now handled with Tailwind CSS classes
const StyledCard = styled(Card) `
  width: 100% !important;
  margin-bottom: 1.5rem !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const CardContent = styled(Card.Content) `
  flex: 1;
  padding: 2rem !important;
  overflow-y: auto;
`;
export function CardsForm({ onSubmit }) {
    let configService = useFluidForm();
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const submitButtonLabel = configService.getAdditionalConfigValue("styleConfig", "submitButtonLabel") || "Submit";
    useRenderViewChange("cards");
    let cards = buildCards(configService);
    const handleSubmit = async () => {
        onSubmit?.();
    };
    const setCardIndex = (index) => {
        if (index >= cards.length || index < 0) {
            return;
        }
        let nextCard = cards[index];
        let nextField = nextCard.data.fieldPath;
        if (nextField) {
            configService.setCurrentField(nextField);
            setCurrentCardIndex(index);
        }
    };
    const handleNext = () => {
        setCardIndex(currentCardIndex + 1);
    };
    const handlePrevious = () => {
        setCardIndex(currentCardIndex - 1);
    };
    const handleCardClick = (index) => {
        setCardIndex(index);
    };
    //If currentField is different from the current card we need to calculate the right active card 
    useEffect(() => {
        if (configService.currentField) {
            // Find the index of the card that contains the current field
            const activeCardIndex = cards.findIndex(card => {
                // Check if the current field is one of the fields inside the card's containerField
                return Object.keys(card.data.value).some((field) => field === configService.currentField);
            });
            // If we found a matching card, set it as active
            if (activeCardIndex !== -1 && activeCardIndex !== currentCardIndex) {
                setCurrentCardIndex(activeCardIndex);
            }
        }
    }, [configService.currentField, cards]);
    return (React.createElement("div", { className: "flex flex-col md:flex-row" },
        React.createElement("div", { className: "md:hidden mb-4 flex items-center justify-between" },
            React.createElement("button", { onClick: () => setShowMobileSidebar(!showMobileSidebar), className: "flex items-center space-x-2 bg-gray-100 px-3 py-2 rounded-md text-gray-700" },
                React.createElement(MenuIcon, { size: 18 }),
                React.createElement("span", null, "Sections")),
            React.createElement("div", { className: "text-sm text-gray-500" },
                "Step ",
                currentCardIndex + 1,
                " of ",
                cards.length)),
        React.createElement("div", { className: `${showMobileSidebar ? 'block' : 'hidden'} md:block md:w-44 lg:w-52 md:pr-4 f-border-r md:mr-4 mb-4 md:mb-0` },
            React.createElement("h3", { className: "text-sm font-medium f-text mb-2" }, "Form Sections"),
            React.createElement("div", { className: "space-y-1" }, cards.map((card, index) => (React.createElement("div", { key: index, className: `p-2.5 rounded-md text-sm cursor-pointer transition-all ${index === currentCardIndex
                    ? 'f-bg-opaque border-l-2 fluid-primary-border-left'
                    : 'hover:f-bg-opaque'}`, onClick: () => handleCardClick(index) }, card.title || `Section ${index + 1}`))))),
        React.createElement("div", { className: "flex-1" },
            React.createElement("div", { className: "hidden text-right text-sm text-gray-500 mb-3" },
                "Step ",
                currentCardIndex + 1,
                " of ",
                cards.length),
            cards.length > 0 && (React.createElement(StyledCard, { className: "f-bg f-border" },
                React.createElement(Card.Header, { style: { padding: '1.5rem' } }, cards[currentCardIndex].title),
                React.createElement(CardContent, null,
                    React.createElement(FluidFormFields, { field: cards[currentCardIndex].data })))),
            React.createElement("div", { className: "flex justify-between mt-6" },
                currentCardIndex > 0 ? (React.createElement(Button, { icon: true, labelPosition: "left", onClick: handlePrevious, className: "bg-gray-600 text-white hover:bg-gray-700" },
                    React.createElement(Icon, { name: "arrow left" }),
                    "Previous")) : (React.createElement("div", null)),
                currentCardIndex < cards.length - 1 ? (React.createElement(Button, { primary: true, icon: true, labelPosition: "right", onClick: handleNext, className: "bg-orange-500 text-white hover:bg-orange-600" },
                    "Next",
                    React.createElement(Icon, { name: "arrow right" }))) : (React.createElement("div", { className: "flex items-center gap-2" },
                    React.createElement(SimpleFormClearButton, null),
                    React.createElement(Button, { primary: true, icon: true, labelPosition: "right", onClick: handleSubmit, className: "fluid-button-primary" },
                        submitButtonLabel,
                        React.createElement(Icon, { name: "checkmark" }))))))));
}
export function buildInmersiveModeCards(configService) {
    let renderView = configService.getRenderView();
    let cards = [];
    let firstFocusableField = configService.getFirstFocusableField();
    let emptyRenderView = {
        ...renderView,
        fieldPath: firstFocusableField,
        value: {},
        cardSection: "root"
    };
    let currentCard = {
        data: { ...emptyRenderView },
        title: "",
        type: "section",
        containerField: { type: "section", key: "root" },
        cardSection: "root"
    };
    for (let [key, fieldRenderView] of Object.entries(renderView.value)) {
        let fieldConfig = configService.getConfig(fieldRenderView.configName);
        if (fieldConfig.type === "composedList") {
            if (currentCard) {
                cards.push(currentCard);
            }
            currentCard = {
                data: fieldRenderView,
                title: fieldConfig.label || "",
                type: "composedList",
                containerField: fieldConfig,
                cardSection: fieldConfig.key
            };
        }
        else if (fieldConfig.type === "section") {
            if (currentCard) {
                cards.push(currentCard);
            }
            currentCard = { data: {
                    ...fieldRenderView,
                    cardSection: fieldConfig.key,
                    value: {}
                }, title: fieldConfig.label || "", type: "section", containerField: fieldConfig };
        }
        else {
            currentCard.data.value[key] = fieldRenderView;
        }
    }
    if (Object.keys(currentCard.data).length > 0) {
        cards.push(currentCard);
    }
    //Filter out empty sections
    cards = cards.filter(card => {
        if (card.type === "composedList") {
            return true;
        }
        else {
            let activeCards = Object.values(card.data.value).filter((value) => value.active);
            return activeCards.length > 0;
        }
    });
    return cards;
}
//Todo: This can be taken out of this class
export function buildCards(configService) {
    let renderView = configService.getRenderView();
    let cards = [];
    let firstFocusableField = configService.getFirstFocusableField();
    let emptyRenderView = {
        ...renderView,
        fieldPath: firstFocusableField,
        value: {},
        cardSection: "root"
    };
    let currentCard = {
        data: { ...emptyRenderView },
        title: "",
        type: "section",
        containerField: { type: "section", key: "root" },
        cardSection: "root"
    };
    for (let [key, fieldRenderView] of Object.entries(renderView.value)) {
        let fieldConfig = configService.getConfig(fieldRenderView.configName);
        if (fieldConfig.type === "composedList") {
            if (currentCard) {
                cards.push(currentCard);
            }
            currentCard = {
                data: fieldRenderView,
                title: fieldConfig.label || "",
                type: "composedList",
                containerField: fieldConfig,
                cardSection: fieldConfig.key
            };
        }
        else if (fieldConfig.type === "section") {
            if (currentCard) {
                cards.push(currentCard);
            }
            currentCard = { data: {
                    ...fieldRenderView,
                    cardSection: fieldConfig.key,
                    value: {}
                }, title: fieldConfig.label || "", type: "section", containerField: fieldConfig };
        }
        else {
            currentCard.data.value[key] = fieldRenderView;
        }
    }
    if (Object.keys(currentCard.data).length > 0) {
        cards.push(currentCard);
    }
    //Filter out empty sections
    cards = cards.filter(card => {
        if (card.type === "composedList") {
            return true;
        }
        else {
            let activeCards = Object.values(card.data.value).filter((value) => value.active);
            return activeCards.length > 0;
        }
    });
    return cards;
}
